<template>
  <div :key="notificationId" class="invited-to-room">
    <transition name="fade">
      <img
        @click="back"
        v-if="sendMessageStep == 1"
        class="back-arrow"
        src="@/assets/sidebar-icons/close-arrow.svg"
        alt="previous step"
      />
    </transition>

    <div class="icons">
      <UserIcon class="user-icon" :userId="data.senderId" userStatus="available" />

      <template v-if="isInDifferentSpace">
        <svg class="icons__ellipsis" width="35" height="24" viewBox="0 0 114 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle class="circle1" cx="12" cy="12" r="12" fill="white"/>
          <circle class="circle2" cx="57" cy="12" r="12" fill="white"/>
          <circle class="circle3" cx="102" cy="12" r="12" fill="white"/>
        </svg>
        <SpaceLogo :logo="spaceLogo" :title="data.data.areaName" diameter="90px"/>
      </template>
    </div>

    <div class="name">
      <span class="name__fullname">{{ data.data.senderName }}</span>
      <span v-if="pronouns" class="name__pronouns">({{ pronouns }})</span>
    </div>

    <newTimesince class="time" :timestamp="`${data.timestamp.indexOf('Z') === -1 ? `${data.timestamp}Z` : data.timestamp }`" />

    <transition name="slide">
      <div key="1" class="page" v-if="sendMessageStep === 0">
        <div class="message">
          <div class="message__text">Please join me in</div>

          <div class="message__room-name">
            {{ data.data.roomName }}
            {{ isInDifferentSpace ? ` in ${data.data.areaName}` : '' }}
          </div>
        </div>

        <footer class="buttons">
          <IconButton :icon="require(`@/assets/global-icons/cross-white.svg`)" @onClick="decline" colour="red" label="Decline" />

          <IconButton
            :icon="require(`@/assets/sidebar-icons/chat.svg`)"
            @onClick="startMessage"
            colour="grey"
            label="Message"
          />

          <IconButton
            :icon="require(`@/assets/notification-icons/camcorder-white.svg`)"
            @onClick="accept"
            colour="green"
            iconSize="32px"
            label="Accept"
          />
        </footer>
      </div>
      <div key="2" class="page" v-else-if="sendMessageStep === 1">
        <div class="message-presets">
          <button
          v-for="(message, i) in presetMessages"
          :key="i"
          class="message-preset"
          @click="pickPresetMessage(i)">
            {{ message }}
          </button>
        </div>
        <div class="divider">or</div>
        <TextInput class="custom-message" inset multiline label="Type a custom message" v-model="customMessage" />
        <FooterButtons>
          <btn :disabled="sendingMessage || !isMessageValid" @onClick="sendMessage()" colour="red">
            <template #text>Send</template>
          </btn>
        </FooterButtons>
      </div>
      <div key="3" class="page" v-else-if="sendMessageStep === 2">
        <img class="message-sent__icon" src="@/assets/images/email-sent.svg" aria-hidden="true"/>
        <div class="message-sent">Message sent!</div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import newTimesince from '@/components/sidebar/newTimesince.vue';
import { IconButton, TextInput, FooterButtons } from '@/framework';
import UserIcon from '@/components/UserIcon.vue';
import SpaceLogo from '@/components/atoms/SpaceLogo.vue';

import { invitePresetMessages } from '@/data/invitePresetMessages';

export default {
  name: 'InvitedToRoom',
  components: {
    UserIcon,
    IconButton,
    newTimesince,
    TextInput,
    FooterButtons,
    SpaceLogo
  },
  props: {
    notificationId: String
  },
  data() {
    return {
      /*
      data: {
        timestamp: '2022-03-22T17:18:30.9203639Z',
        title: 'Josh Lee',
        message: "Please join me in Josh Lee 3's Room",
        senderId: 'ad882dfe-0627-4dcd-bf4d-33fa5651b002',
        data: {
          roomName: "Josh Lee 3's Room",
          senderName: 'Josh lee 3'
        },
        response: 'Will join you in 10 min',
        status: 'missed'
      },*/


      // 0 - main screen
      // 1 - choose message
      // 2 - sent message
      sendMessageStep: 0,
      customMessage: '',
      presetMessages: invitePresetMessages,
      destroyed: false,
      sendingMessage: false,
      cachedData: null,
      freezeNotificationUpdate: false,
      spaceLogo: null,
    };
  },
  computed: {
    ... mapGetters(['getCurrentNotification', 'getCurrentAreaId']),
    data() {
      if (this.freezeNotificationUpdate) {
        return this.cachedData
      }

      const current = this.getCurrentNotification

      if (current?.id == this.notificationId) {
        return current
      } else {
        return undefined
      }
    },
    pronouns() {
      if (this.data?.data.senderPronouns && this.data?.data.senderPronouns !== '') {
        return this.data.data.senderPronouns
      } else {
        return undefined
      }
    },
    isMessageValid() {
      return this.customMessage.length > 0 && this.customMessage.length < 1000
    },
    spaceId() {
      return this.data.locationId
    },
    isInDifferentSpace() {
      return this.spaceId !== this.getCurrentAreaId
    }
  },
  created() {
    this.$emit('modalSettings', {
      styles: {
        'min-width': '350px',
        'max-width': '350px'
      },
      fullScreenOnMobile: false,
      hasCloseButton: false
    });
  },
  async mounted() {
    this.spaceLogo = await this.requestSpaceLogoUrl(this.spaceId)
  },
  watch: {
    data: {
      handler(newVal) {
        if (this.freezeNotificationUpdate) return  // We don't want to response to updates right now, just return

        // Cache a copy of notification data from store
        // We can freeze the updates, so we hold onto the last data
        if (!this.freezeNotificationUpdate) {
          this.cachedData = this.data
        }

        // Notification data has been updated whilst modal is showing

        if (newVal?.status == "missed") {
          // call was missed, this is invalid now, close the modal
          this['modal/close']()
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions(['modal/close', 'acceptInviteHandler', 'declineInviteHandler', 'messageInviteHandler', 'clearCurrentNotification']),
    ...mapActions({'requestSpaceLogoUrl': 'api/media/requestSpaceLogoUrl'}),
    back() {
      this.sendMessageStep = 0;
    },
    async decline() {
      this['modal/close']();
      await this.declineInviteHandler(this.data);
    },
    async accept() {
      this['modal/close']();
      await this.acceptInviteHandler(this.data);
    },
    startMessage() {
      this.sendMessageStep = 1;
    },
    pickPresetMessage(index) {
      this.customMessage = this.presetMessages[index];
      this.sendMessage()
    },
    async sendMessage() {
      // We prevent the notification data from becoming null, which will happen once we send a response
      // so we can show the component still
      this.freezeNotificationUpdate = true

      this.sendingMessage = true

      const res = await this.messageInviteHandler([this.data, this.customMessage])

      if (res.success === undefined) {
        this['modal/close']()
        return
      }

      this.sendMessageStep = 2;
      this.closeAfterPause();
    },
    closeAfterPause() {
      setTimeout(() => {
        if (!this.destroyed) {
          this['modal/close']();
        }
      }, 2000);
    }
  },
  beforeDestroy() {
    this.destroyed = true;
    this.freezeNotificationUpdate = true

    if (this.getCurrentNotification?.id == this.notificationId) {
      this.clearCurrentNotification()
    }
  }
};
</script>
<style scoped lang="scss">
.slide-enter-active,
.slide-leave-active {
  transition: 0.3s;
  overflow: hidden;
}

.slide-enter /* .fade-leave-active below version 2.1.8 */ {
  transform: translateX(500%);
  max-height: 0;
}

.slide-enter-to {
  max-height: 1000px;
}

.slide-leave {
  max-height: 1000px;
}
.slide-leave-to {
  transform: translateX(-500%);
  max-height: 0px;
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.user-icon {
  width: 80px;
  height: 80px;
}

.footer-buttons {
  width: 100%;
}

.message-presets {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 10px;
}

.message-preset {
  border: none;
  background-color: $grey-7;
  color: $grey-3;
  font-size: 0.875rem; /** 14px */
  width: 100%;
  display: block;
  padding: 12px;
  border-radius: 50px;
  font-weight: bold;
  cursor: pointer;
  transition: 0.2s;

  &:hover {
    background-color: $grey-3;
    color: white;
  }
}

.custom-message {
  margin-top: 10px;
  box-sizing: border-box;
  width: 100%;
}

.invited-to-room,
.page {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.page {
  box-sizing: border-box;
  padding-left: 10px;
  padding-right: 10px;
}

.name {
  text-align: center;

  &__fullname {
    font-size: 1.125rem; /** 18px */
    color: white;
    font-weight: bold;
    margin-right: 8px;
    vertical-align: middle;
  }

  &__pronouns {
    color: $grey-1;
    font-size: 1rem;
    vertical-align: middle;
  }
}

.time {
  font-size: 0.875rem; /** 14px */
  font-weight: bold;
  color: $grey-1;
}

.message {
  text-align: center;
  margin-top: 20px;
  &__text {
    font-size: 1rem;
    color: $grey-1;
  }

  &__room-name {
    font-weight: bold;
    font-size: 1.5rem; /** 24px */
    color: white;
  }
}

.divider {
  display: flex;
  justify-content: center;
  align-items: center;
  color: $grey-3;
  font-weight: bold;

  &:before,
  &:after {
    content: '';
    display: block;
    height: 1px;
    width: 100px;
    background-color: $grey-3;
    margin-right: 20px;
    margin-left: 20px;
    position: relative;
    top: 2px;
  }
}

.buttons {
  margin-top: 20px;
  width: 50%;
  display: flex;
  justify-content: space-around;
  width: 90%;

  .icon-button {
    flex-basis: 33%;
  }
}

.back-arrow {
  cursor: pointer;
  height: 32px;
  width: 32px;
  position: absolute;
  padding: 15px;
  top: 15px;
  left: 15px;
}

.message-sent {
  font-size: 1.125rem; /** 18px */
  color: $grey-1;
  font-weight: bold;
  padding-bottom: 20px;

  &__icon {
    padding-top: 40px;
    padding-bottom: 100px;
  }
}

.icons {
  display: flex;
  width: 80%;
  justify-content: space-around;
  gap: 5px;
  align-items: center;

  &__ellipsis {
    .circle1 {
      animation: ellipsis 1.5s infinite;
      animation-delay: 200ms;
    }

    .circle2 {
      animation: ellipsis 1.5s infinite;
      animation-delay: 400ms;
    }

    .circle3 {
      animation: ellipsis 1.5s infinite;
      animation-delay: 600ms;
    }
  }
}

@keyframes ellipsis {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.2;
  }
  100% {
    opacity: 1;
  }
}


</style>
